import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToId } from "../lib/scrollTo";
import content from "../content";

const SLIDE_MS = 5000; // 5 segundos por foto

export default function Hero() {
  const reduced = useReducedMotion();
  const { hero, brand } = content;

  const images = (hero.bgImages?.length ? hero.bgImages : hero.heroPhoto ? [hero.heroPhoto] : []).map((i) =>
    typeof i === "string" ? { src: i } : i
  );
  // layout "photo": texto más arriba y fondo más claro para que se vean las caras.
  const photo = hero.layout === "photo";
  const [index, setIndex] = useState(0);
  // Con una sola foto hacemos un recorrido vertical lento (para verla completa);
  // con varias, se funden en bucle.
  const singleImage = images.length === 1;
  const panMode = hero.panMode || "reveal";
  const doPan = singleImage && !reduced;
  const slideZoom = !singleImage && !reduced; // zoom suave en cada foto del carrusel

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, SLIDE_MS);
    return () => clearInterval(timer);
  }, [images.length]);

  const [focusX, focusY] = (images[index]?.focus || "50% 50%").split(" ");

  return (
    <section
      id="inicio"
      className="relative h-[100svh] min-h-[620px] w-full overflow-hidden bg-ink"
      data-testid="hero-section"
    >
      {/* Fondo: una sola foto hace un lento recorrido vertical para mostrarse
          completa; varias fotos se funden suavemente cada 5 segundos. */}
      {images.length > 0 && (
        <AnimatePresence>
          <motion.div
            key={images[index].src}
            initial={{ opacity: 0 }}
            animate={{ opacity: photo ? 0.92 : 0.55 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduced ? 0 : 1.6, ease: "easeInOut" }}
            className="absolute inset-0"
            data-testid="hero-bg-image"
          >
            <motion.div
              role="img"
              aria-label={hero.heroPhotoAlt}
              className="absolute inset-0 bg-cover bg-no-repeat"
              style={{
                backgroundImage: `url(${images[index].src})`,
                backgroundPositionX: focusX,
                backgroundPositionY: doPan && panMode === "reveal" ? "0%" : focusY,
                willChange: "transform, background-position",
              }}
              animate={
                doPan
                  ? panMode === "kenburns"
                    ? {
                        scale: [1, 1.08, 1],
                        backgroundPositionY: [focusY, `${parseFloat(focusY) + 6}%`, focusY],
                      }
                    : { backgroundPositionY: ["0%", "100%", "0%"] }
                  : {}
              }
              transition={
                doPan
                  ? {
                      duration: panMode === "kenburns" ? 20 : 26,
                      repeat: Infinity,
                      ease: "easeInOut",
                      times: [0, 0.5, 1],
                    }
                  : {}
              }
            />
          </motion.div>
        </AnimatePresence>
      )}
      {photo ? (
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,5,0.8) 0%, rgba(5,5,5,0.4) 32%, rgba(5,5,5,0.08) 58%, rgba(5,5,5,0.35) 86%, #050505 100%)",
          }}
          aria-hidden="true"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/45 to-ink" />
      )}
      <div
        className="absolute inset-0"
        style={{
          background: photo
            ? "radial-gradient(ellipse 70% 40% at 50% 48%, rgba(5,5,5,0.45), transparent 70%)"
            : "radial-gradient(circle at 50% 46%, rgba(5,5,5,0.35), transparent 62%)",
        }}
        aria-hidden="true"
      />

      {/* Logo top center */}
      <div className="absolute top-6 inset-x-0 z-20 flex justify-center pointer-events-none">
        <motion.img
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          src={brand.logo}
          alt={`Logo ${brand.name}`}
          className="h-16 md:h-20 w-auto opacity-90"
          data-testid="hero-logo"
        />
      </div>

      {/* Overlay content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mb-7"
          data-testid="hero-week-badge"
        >
          <span className="relative inline-flex items-center gap-2.5 rounded-full border border-electric/50 bg-electric/10 backdrop-blur-md px-5 py-2.5 font-poppins text-xs md:text-sm font-semibold tracking-[0.28em] uppercase text-white animate-pulse-ring">
            <span className="w-2 h-2 rounded-full bg-electric shadow-[0_0_10px_rgba(33,112,232,1)]" />
            {hero.weekBadge}
          </span>
        </motion.div>

        <h1
          aria-label={`${hero.titleTop} ${hero.titleBottom}`}
          className={`display text-white ${
            photo
              ? "text-[clamp(3.25rem,15vw,9rem)] drop-shadow-[0_6px_30px_rgba(0,0,0,0.7)]"
              : "text-[clamp(3.25rem,15vw,12.5rem)]"
          }`}
        >
          <motion.span
            className="block"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            {hero.titleTop}
          </motion.span>
          <motion.span
            className="block text-electric text-glow-blue"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            {hero.titleBottom}
          </motion.span>
        </h1>

        {hero.gatewayPill && (
          <motion.button
            type="button"
            onClick={() => scrollToId(hero.gatewayPill.target)}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            data-testid="hero-gateway-pill"
            className="group mt-8 md:mt-10 inline-flex max-w-full items-center gap-2 md:gap-3 whitespace-nowrap rounded-full border border-white/20 bg-white/[0.07] backdrop-blur-md pl-1.5 pr-4 md:pr-5 py-1.5 font-poppins text-[11px] md:text-sm font-semibold uppercase tracking-[0.08em] md:tracking-[0.16em] text-white transition-colors duration-300 hover:bg-electric hover:border-electric cursor-pointer"
          >
            <span className="rounded-full bg-electric px-3 py-1.5 text-[10px] md:text-xs tracking-[0.1em] md:tracking-[0.18em] transition-colors duration-300 group-hover:bg-white group-hover:text-electric">
              {hero.gatewayPill.badge}
            </span>
            {hero.gatewayPill.label}
            <ArrowRight size={16} className="hidden sm:block transition-transform duration-300 group-hover:translate-x-1" />
          </motion.button>
        )}
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2"
        data-testid="hero-scroll-indicator"
      >
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-1.5">
          <span className="w-1 h-2 rounded-full bg-electric animate-scroll-dot" />
        </div>
        <span className="font-poppins text-[11px] tracking-[0.35em] uppercase text-white/60">
          {hero.scrollHint}
        </span>
      </motion.div>
    </section>
  );
}
